import {Component, OnDestroy, OnInit, ViewEncapsulation} from '@angular/core';
import {Observable, Subject, BehaviorSubject} from 'rxjs';
import {takeUntil, shareReplay} from 'rxjs/operators';
import {FuseConfigService} from '@fuse/services/config.service';
import {navigation} from '@app/components-fuse/navigation/navigation';
import {select, Store} from '@ngrx/store';
import {AppState, selectAuthState} from '@store/app.states';
import {OnDestroyMixin, untilComponentDestroyed} from '@w11k/ngx-componentdestroyed';
import {isDashboardViewFullScreenState} from '@app/components/projects/surveys/results/dashboard/widgets/store/dashboard-widget.seletors';
import {FuseSidebarService} from '../../../../../@fuse/components/sidebar/sidebar.service';
import {State as authState} from '@store/reducers/auth.reducers';
import {fuseConfig} from '@app/components-fuse/fuse-config';

@Component({
    selector: 'vertical-layout-1',
    templateUrl: './layout-1.component.html',
    styleUrls: ['./layout-1.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class VerticalLayout1Component extends OnDestroyMixin implements OnInit, OnDestroy {
  public getState$: Observable<authState> = this.store.select(selectAuthState).pipe(
    shareReplay({bufferSize: 1, refCount: true})
  );

  fuseConfig: any;
  navigation: any;

  isLoggedToFirebase = false;

  displayNavBar = true;
  navbarOpened$ = new BehaviorSubject<boolean>(false);

  // Private
  private _unsubscribeAll: Subject<any>;

  /**
   * Constructor
   *
   * @param {FuseConfigService} _fuseConfigService
   * @param _fuseSidebarService
   * @param store
   */
  constructor(
    private _fuseConfigService: FuseConfigService,
    private _fuseSidebarService: FuseSidebarService,
    private store: Store<AppState>,
  ) {
    super();
    // Set the defaults
    this.navigation = navigation;
    // Initialize fuseConfig with default config to ensure navbar is rendered
    this.fuseConfig = fuseConfig;
    // Set the private defaults
    this._unsubscribeAll = new Subject();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  ngOnInit(): void {

    this.store
      .pipe(
        untilComponentDestroyed(this),
        select(isDashboardViewFullScreenState),
      )
      .subscribe((data) => {

        if (data) {
        }
        this.displayNavBar = !data;

      });

    // Subscribe to config changes
    this._fuseConfigService.config
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((config) => {
        this.fuseConfig = config;
      });

    // Subscribe to navbar state changes
    this._subscribeToNavbarState();
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  /**
   * Subscribe to navbar state changes
   */
  private _subscribeToNavbarState(): void {
    // Check periodically if navbar exists and subscribe to its changes
    const checkInterval = setInterval(() => {
      const navbar = this._fuseSidebarService.getSidebar('navbar');

      if (navbar) {
        navbar.opened = true;
        navbar.folded = true;
        // Clear the true once we have the navbar
        clearInterval(checkInterval);

        // Subscribe to openedChanged events
        navbar.openedChanged
          .pipe(takeUntil(this._unsubscribeAll))
          .subscribe((isOpened: boolean) => {
            // Consider navbar "opened" only if it's opened AND not folded
            const isReallyOpened = isOpened && !navbar.folded;
            this.navbarOpened$.next(isReallyOpened);
          });

        // Subscribe to foldedChanged events too
        navbar.foldedChanged
          .pipe(takeUntil(this._unsubscribeAll))
          .subscribe((isFolded: boolean) => {
            // Consider navbar "opened" only if it's opened AND not folded
            const isReallyOpened = navbar.opened && !isFolded;
            this.navbarOpened$.next(isReallyOpened);
          });

        // Set initial state
        const initialOpened = navbar.opened && !navbar.folded;
        this.navbarOpened$.next(initialOpened);
      }
    }, 100);

    // Clean up interval after 10 seconds to avoid infinite checking
    setTimeout(() => clearInterval(checkInterval), 10000);
  }
}
